<div class="flex h-full flex-col bg-white">

  <!-- ── Loading skeleton ────────────────────────────────────────────── -->
  <ng-container *ngIf="loading">
    <div class="animate-pulse flex-1 p-5">
      <!-- Identity skeleton -->
      <div class="mb-1 h-2.5 w-28 rounded bg-zinc-100"></div>
      <div class="mb-4 flex items-center justify-between">
        <div class="h-4 w-40 rounded bg-zinc-100"></div>
        <div class="h-6 w-6 rounded bg-zinc-100"></div>
      </div>
      <div class="mb-1 h-2.5 w-48 rounded bg-zinc-100"></div>
      <div class="mb-4 flex gap-1.5">
        <div class="h-5 w-10 rounded bg-zinc-100"></div>
        <div class="h-5 w-12 rounded bg-zinc-100"></div>
      </div>
      <!-- Section skeletons -->
      <div *ngFor="let _ of SKELETON_ITEMS" class="mb-4 rounded-lg border border-zinc-100 p-4">
        <div class="mb-3 h-2.5 w-24 rounded bg-zinc-100"></div>
        <div class="mb-1.5 h-3 w-full rounded bg-zinc-100"></div>
        <div class="h-3 w-4/5 rounded bg-zinc-100"></div>
      </div>
    </div>
  </ng-container>

  <!-- ── Error state ──────────────────────────────────────────────────── -->
  <ng-container *ngIf="!loading && error">
    <div class="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
      <lucide-icon name="circle-alert" [size]="28" [strokeWidth]="1.25" class="text-red-400"></lucide-icon>
      <p class="text-xs font-medium text-zinc-700">{{ error }}</p>
      <button
        (click)="refresh()"
        class="text-xs font-medium text-violet-600 hover:underline"
      >Retry</button>
      <button
        (click)="close()"
        class="text-[11px] text-zinc-400 hover:text-zinc-600"
      >Dismiss</button>
    </div>
  </ng-container>

  <!-- ── Content ──────────────────────────────────────────────────────── -->
  <ng-container *ngIf="!loading && !error && context">

    <!-- ── Section 1: Identity header ──────────────────────────────── -->
    <div class="flex-shrink-0 border-b border-zinc-200 px-5 py-4">

      <!-- Label row + refresh indicator + close button -->
      <div class="mb-3 flex items-center justify-between">
        <div class="flex items-center gap-1.5">
          <span class="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
            Organization Context
          </span>
          <lucide-icon
            *ngIf="contextRefreshing"
            name="refresh-cw"
            [size]="10"
            [strokeWidth]="1.75"
            class="animate-spin text-zinc-400"
          ></lucide-icon>
        </div>
        <button
          (click)="close()"
          title="Close"
          class="flex h-6 w-6 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600"
        >
          <lucide-icon name="circle-x" [size]="14" [strokeWidth]="1.75"></lucide-icon>
        </button>
      </div>

      <!-- Org name -->
      <p class="text-sm font-semibold text-zinc-900">{{ context.organization.name }}</p>

      <!-- Org ID in monospace -->
      <p class="mt-0.5 truncate font-mono text-[10px] text-zinc-400">
        {{ context.organization.orgId }}
      </p>

      <!-- Status badges -->
      <div class="mt-2 flex flex-wrap items-center gap-1.5">
        <span
          class="rounded px-1.5 py-0.5 text-[10px] font-semibold"
          [ngClass]="tierBadgeClass(context.organization.subscriptionTier)"
        >{{ context.organization.subscriptionTier }}</span>
        <span
          class="rounded px-1.5 py-0.5 text-[10px] font-semibold"
          [ngClass]="statusBadgeClass(context.organization.subscriptionStatus)"
        >{{ statusLabel(context.organization.subscriptionStatus) }}</span>
      </div>
    </div>

    <!-- ── Tab bar ────────────────────────────────────────────────────── -->
    <div class="flex-shrink-0 border-b border-zinc-200">
      <nav class="flex px-2">

        <button
          (click)="selectTab('overview')"
          class="border-b-2 px-2 py-2.5 text-[11px] font-medium transition-colors"
          [class.border-violet-600]="activeTab === 'overview'"
          [class.text-violet-700]="activeTab === 'overview'"
          [class.border-transparent]="activeTab !== 'overview'"
          [class.text-zinc-500]="activeTab !== 'overview'"
          [class.hover:text-zinc-700]="activeTab !== 'overview'"
        >Overview</button>

        <button
          (click)="selectTab('team')"
          class="border-b-2 px-2 py-2.5 text-[11px] font-medium transition-colors"
          [class.border-violet-600]="activeTab === 'team'"
          [class.text-violet-700]="activeTab === 'team'"
          [class.border-transparent]="activeTab !== 'team'"
          [class.text-zinc-500]="activeTab !== 'team'"
          [class.hover:text-zinc-700]="activeTab !== 'team'"
        >Team <span class="ml-0.5 text-zinc-400">({{ context.teamMembers.length }})</span></button>

        <button
          (click)="selectTab('services')"
          class="border-b-2 px-2 py-2.5 text-[11px] font-medium transition-colors"
          [class.border-violet-600]="activeTab === 'services'"
          [class.text-violet-700]="activeTab === 'services'"
          [class.border-transparent]="activeTab !== 'services'"
          [class.text-zinc-500]="activeTab !== 'services'"
          [class.hover:text-zinc-700]="activeTab !== 'services'"
        >Services <span class="ml-0.5 text-zinc-400">({{ context.monitoredServices.length }})</span></button>

        <button
          (click)="selectTab('monitoring')"
          class="border-b-2 px-2 py-2.5 text-[11px] font-medium transition-colors"
          [class.border-violet-600]="activeTab === 'monitoring'"
          [class.text-violet-700]="activeTab === 'monitoring'"
          [class.border-transparent]="activeTab !== 'monitoring'"
          [class.text-zinc-500]="activeTab !== 'monitoring'"
          [class.hover:text-zinc-700]="activeTab !== 'monitoring'"
        >
          Monitoring
          <ng-container *ngIf="monitoringContext && monitoringContext.summary.degradedCount > 0">
            <span class="ml-0.5 rounded-full bg-red-100 px-1 py-0.5 text-[9px] font-semibold text-red-700">
              {{ monitoringContext.summary.degradedCount }}
            </span>
          </ng-container>
        </button>

      </nav>
    </div>

    <!-- ── Tab: Overview ──────────────────────────────────────────────── -->
    <div *ngIf="activeTab === 'overview'" class="flex-1 space-y-4 overflow-y-auto px-5 py-4">

      <!-- ── Section 2: Subscription Summary ──────────────────────── -->
      <section>
        <p class="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
          Subscription
        </p>

        <div class="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3">
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
            <span>
              <span class="font-semibold text-zinc-800">{{ context.teamSize }}</span>
              {{ context.teamSize === 1 ? 'member' : 'members' }}
            </span>
            <span class="text-zinc-300">·</span>
            <span>
              <span class="font-semibold text-zinc-800">{{ context.monitoredServicesCount }}</span>
              / {{ tierLimit(context.organization.subscriptionTier) }} monitored
            </span>
          </div>
          <div class="mt-1 text-[11px]" [class.text-zinc-500]="context.organization.nextRenewalDate" [class.text-zinc-400]="!context.organization.nextRenewalDate">
            <ng-container *ngIf="context.organization.nextRenewalDate">
              Renewal <span class="font-medium text-zinc-700">{{ formatDate(context.organization.nextRenewalDate) }}</span>
            </ng-container>
            <ng-container *ngIf="!context.organization.nextRenewalDate">No renewal date set</ng-container>
          </div>

          <div class="mt-3">
            <button
              *ngIf="!editMode"
              (click)="openEdit()"
              class="text-[11px] font-medium text-violet-600 transition-colors hover:text-violet-800"
            >
              Manage Subscription
            </button>
          </div>
        </div>

        <!-- Inline edit form (shown when editMode) -->
        <div *ngIf="editMode" class="mt-2 rounded-lg border border-violet-200 bg-white px-4 py-3">

          <!-- Save error -->
          <div
            *ngIf="saveError"
            class="mb-3 flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700"
          >
            <lucide-icon name="circle-alert" [size]="12" class="mt-0.5 flex-shrink-0" [strokeWidth]="2"></lucide-icon>
            {{ saveError }}
          </div>

          <div class="space-y-3">

            <!-- Tier -->
            <div>
              <label class="mb-1 block text-[10px] font-semibold text-zinc-600">Tier</label>
              <select
                [(ngModel)]="editForm.tier"
                class="block w-full rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs text-zinc-900 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-100"
              >
                <option *ngFor="let t of tierOptions" [value]="t">{{ t }}</option>
              </select>
            </div>

            <!-- Status -->
            <div>
              <label class="mb-1 block text-[10px] font-semibold text-zinc-600">Status</label>
              <select
                [(ngModel)]="editForm.status"
                class="block w-full rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs text-zinc-900 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-100"
              >
                <option *ngFor="let s of statusOptions" [value]="s">{{ statusLabel(s) }}</option>
              </select>
            </div>

            <!-- Next renewal -->
            <div>
              <label class="mb-1 block text-[10px] font-semibold text-zinc-600">Next Renewal</label>
              <input
                type="date"
                [(ngModel)]="editForm.nextRenewalDate"
                class="block w-full rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs text-zinc-900 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-100"
              />
              <p class="mt-0.5 text-[10px] text-zinc-400">Leave blank to clear.</p>
            </div>

          </div>

          <!-- Form actions -->
          <div class="mt-3 flex items-center justify-end gap-2">
            <button
              (click)="cancelEdit()"
              [disabled]="saving"
              class="rounded-md border border-zinc-200 px-3 py-1 text-[11px] font-medium text-zinc-600 transition-colors hover:bg-zinc-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              (click)="saveEdit()"
              [disabled]="saving"
              class="flex items-center gap-1 rounded-md bg-violet-600 px-3 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-violet-700 disabled:opacity-60"
            >
              <lucide-icon *ngIf="saving" name="loader" [size]="11" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
              {{ saving ? 'Saving…' : 'Save' }}
            </button>
          </div>

        </div>
      </section>

      <!-- ── Section 3: Active Integrations ────────────────────────── -->
      <section>
        <p class="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
          Integrations
        </p>

        <!-- Empty: no integrations returned -->
        <div
          *ngIf="context.integrations.length === 0"
          class="rounded-lg border border-zinc-100 bg-zinc-50 px-4 py-3 text-[11px] text-zinc-400"
        >
          No integrations found for this organization.
        </div>

        <div *ngIf="context.integrations.length > 0" class="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
          <div
            *ngFor="let intg of context.integrations"
            class="flex items-center gap-3 px-4 py-2.5 transition-opacity"
            [class.opacity-40]="intg.status === 'disconnected'"
          >
            <!-- Provider icon -->
            <lucide-icon
              [name]="integrationIcon(intg.provider)"
              [size]="14"
              [strokeWidth]="1.75"
              class="flex-shrink-0 text-zinc-500"
            ></lucide-icon>

            <!-- Label -->
            <div class="min-w-0 flex-1">
              <p class="text-xs font-medium text-zinc-800">{{ integrationLabel(intg.provider) }}</p>
              <p *ngIf="integrationMeta(intg) as meta" class="truncate text-[10px] text-zinc-400">{{ meta }}</p>
            </div>

            <!-- Status indicator -->
            <div class="flex flex-shrink-0 items-center gap-1">
              <span
                class="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                [ngClass]="integrationDotClass(intg.status)"
              ></span>
              <span
                class="text-[10px] font-medium"
                [ngClass]="integrationStatusClass(intg.status)"
              >{{ integrationStatusLabel(intg.status) }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- ── Section 3b: AWS accounts (multi-account inventory) ─────── -->
      <section *ngIf="awsSummary">
        <div class="mb-2 flex items-center justify-between">
          <p class="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">AWS accounts</p>
          <span
            class="rounded-full px-1.5 py-0.5 text-[9px] font-semibold"
            [ngClass]="{
              'bg-emerald-100 text-emerald-700': awsOverallState === 'healthy',
              'bg-amber-100 text-amber-700':     awsOverallState === 'degraded',
              'bg-zinc-100 text-zinc-500':       awsOverallState === 'none'
            }"
          >{{ awsOverallState === 'healthy' ? 'Healthy' : awsOverallState === 'degraded' ? 'Degraded' : 'Not connected' }}</span>
        </div>

        <!-- No AWS connected -->
        <div
          *ngIf="awsAccounts.length === 0"
          class="rounded-lg border border-zinc-100 bg-zinc-50 px-4 py-3 text-[11px] text-zinc-400"
        >
          No AWS accounts connected.
        </div>

        <!-- Accounts present but none primary — support-visible integrity warning -->
        <div
          *ngIf="awsAccounts.length > 0 && awsSummary && !awsSummary.hasPrimary"
          class="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] text-amber-800"
        >
          Accounts are connected but none is marked primary.
        </div>

        <!-- Per-account cards: primary first -->
        <div *ngIf="awsAccounts.length > 0" class="space-y-2">
          <div
            *ngFor="let a of awsAccounts"
            class="rounded-lg border bg-white px-3 py-2.5"
            [class.border-violet-200]="a.isPrimary"
            [class.border-zinc-200]="!a.isPrimary"
          >
            <!-- account id/label + primary + status -->
            <div class="flex items-center gap-2">
              <span class="h-1.5 w-1.5 flex-shrink-0 rounded-full" [ngClass]="awsHealthDotClass(a.healthStatus)" [title]="awsHealthLabel(a.healthStatus)"></span>
              <span class="min-w-0 flex-1 truncate font-mono text-xs font-medium text-zinc-800" [title]="a.accountId">{{ awsAccountLabel(a) }}</span>
              <span *ngIf="a.isPrimary" class="flex-shrink-0 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700">Primary</span>
              <span class="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold" [ngClass]="awsStatusBadgeClass(a.connectionStatus)">{{ awsStatusLabel(a.connectionStatus) }}</span>
            </div>

            <!-- per-account regions + monitoring footprint -->
            <div class="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-zinc-500">
              <span class="inline-flex items-center gap-1">
                <lucide-icon name="map-pin" [size]="10" [strokeWidth]="1.75"></lucide-icon>{{ awsRegionSummary(a) }}
              </span>
              <span class="text-zinc-300">·</span>
              <span><span class="font-semibold text-zinc-700">{{ a.monitoredResourceCount }}</span> monitored</span>
              <ng-container *ngIf="a.serviceEntryCount">
                <span class="text-zinc-300">·</span>
                <span><span class="font-semibold text-zinc-700">{{ a.serviceEntryCount }}</span> resources</span>
              </ng-container>
              <ng-container *ngIf="a.healthStatus !== 'OK'">
                <span class="text-zinc-300">·</span>
                <span class="text-amber-600">{{ awsHealthLabel(a.healthStatus) }}</span>
              </ng-container>
            </div>

            <!-- role ARN (for support) + failure reason -->
            <div *ngIf="a.roleArn" class="mt-1 truncate font-mono text-[9px] text-zinc-400" [title]="a.roleArn">{{ a.roleArn }}</div>
            <div *ngIf="a.failureReason" class="mt-1 text-[10px] text-red-600">{{ a.failureReason }}</div>

            <!-- Per-account support actions -->
            <div class="mt-2 flex items-center gap-2 border-t border-zinc-100 pt-2">
              <button
                type="button"
                (click)="verifyAwsAccount(a)"
                [disabled]="!!awsActionAccountId"
                class="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium text-zinc-500 transition-colors hover:text-violet-600 disabled:opacity-50"
              >
                <lucide-icon name="refresh-cw" [size]="11" [strokeWidth]="2" [class.animate-spin]="awsActionAccountId === a.accountId"></lucide-icon>
                Verify
              </button>
              <button
                type="button"
                (click)="awsEditRegionsAccountId === a.accountId ? cancelEditAwsRegions() : startEditAwsRegions(a)"
                [disabled]="!!awsActionAccountId"
                class="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium text-zinc-500 transition-colors hover:text-violet-600 disabled:opacity-50"
              >Manage regions</button>
              <button
                type="button"
                (click)="askDisconnectAwsAccount(a)"
                [disabled]="!!awsActionAccountId"
                class="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium text-zinc-400 transition-colors hover:text-red-600 disabled:opacity-50"
              >Disconnect</button>
            </div>

            <!-- Inline per-account region editor -->
            <div *ngIf="awsEditRegionsAccountId === a.accountId" class="mt-2 space-y-2 rounded-md border border-zinc-200 bg-zinc-50 p-2">
              <div class="grid grid-cols-2 gap-1">
                <label
                  *ngFor="let r of SUPPORTED_REGIONS"
                  class="flex items-center gap-1.5 rounded border bg-white px-2 py-1 text-[10px]"
                  [class.border-violet-300]="isAwsRegionEnabled(r)"
                  [class.border-zinc-200]="!isAwsRegionEnabled(r)"
                >
                  <input type="checkbox" class="h-3 w-3 rounded border-zinc-300 text-violet-600" [checked]="isAwsRegionEnabled(r)" [disabled]="r === awsAcctDefaultRegion" (change)="toggleAwsRegion(r)" />
                  <span class="font-mono">{{ r }}</span>
                  <span *ngIf="r === awsAcctDefaultRegion" class="ml-auto text-[9px] font-semibold text-violet-600">default</span>
                </label>
              </div>
              <div class="flex items-center gap-2">
                <label class="text-[10px] font-medium text-zinc-600">Default</label>
                <select [(ngModel)]="awsAcctDefaultRegion" class="rounded border border-zinc-200 bg-white px-1.5 py-1 text-[10px]">
                  <option *ngFor="let r of awsAcctEnabledRegions" [value]="r">{{ r }}</option>
                </select>
                <button type="button" (click)="cancelEditAwsRegions()" class="ml-auto rounded-md border border-zinc-200 bg-white px-2 py-1 text-[10px] font-medium text-zinc-600">Cancel</button>
                <button type="button" (click)="saveAwsRegions(a)" [disabled]="awsActionAccountId === a.accountId" class="rounded-md bg-violet-600 px-2 py-1 text-[10px] font-semibold text-white disabled:opacity-60">Save</button>
              </div>
              <p *ngIf="awsAcctRegionsError" class="text-[10px] text-red-600">{{ awsAcctRegionsError }}</p>
            </div>
          </div>
        </div>

        <!-- Action result / error banner -->
        <div *ngIf="awsActionResult" class="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[10px] text-emerald-800">{{ awsActionResult }}</div>
        <div *ngIf="awsActionError" class="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[10px] text-red-700">{{ awsActionError }}</div>

        <!-- Recent AWS lifecycle activity (audit trail) -->
        <div *ngIf="awsEvents.length > 0" class="mt-3">
          <p class="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Recent AWS activity</p>
          <div class="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
            <div *ngFor="let ev of awsEvents.slice(0, 8)" class="flex items-start gap-2 px-3 py-2">
              <span class="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full" [ngClass]="awsEventDotClass(ev.status)"></span>
              <div class="min-w-0 flex-1">
                <p class="text-[11px] font-medium text-zinc-800">
                  {{ awsEventLabel(ev.eventType) }}
                  <span *ngIf="ev.accountId" class="ml-1 font-mono text-[10px] text-zinc-400">{{ ev.accountId }}</span>
                </p>
                <p class="text-[10px] text-zinc-400">{{ ev.timestamp | date:'MMM d, HH:mm' }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ── Section 3c: Azure subscriptions (read-only inventory) ───── -->
      <section *ngIf="azureSummary">
        <div class="mb-2 flex items-center justify-between">
          <p class="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Azure subscriptions</p>
          <span class="rounded-full px-1.5 py-0.5 text-[9px] font-semibold" [ngClass]="{
            'bg-emerald-100 text-emerald-700': azureOverallState === 'healthy',
            'bg-amber-100 text-amber-700':     azureOverallState === 'degraded',
            'bg-zinc-100 text-zinc-500':       azureOverallState === 'none'
          }">{{ azureOverallState === 'healthy' ? 'Healthy' : azureOverallState === 'degraded' ? 'Degraded' : 'Not connected' }}</span>
        </div>

        <p *ngIf="azureSubscriptions.length === 0" class="rounded-lg border border-dashed border-zinc-200 px-3 py-2 text-[11px] text-zinc-400">
          No Azure subscriptions connected.
        </p>

        <div *ngIf="azureSubscriptions.length > 0" class="space-y-2">
          <div *ngFor="let s of azureSubscriptions"
               class="rounded-lg border bg-white px-3 py-2"
               [class.border-violet-200]="s.isPrimary"
               [class.border-zinc-200]="!s.isPrimary">
            <div class="flex items-center gap-2">
              <span class="h-1.5 w-1.5 flex-shrink-0 rounded-full" [ngClass]="awsHealthDotClass(s.healthStatus)"></span>
              <span class="min-w-0 flex-1 truncate font-mono text-xs font-medium text-zinc-800">{{ azureSubscriptionLabel(s) }}</span>
              <span *ngIf="s.isPrimary" class="flex-shrink-0 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700">Primary</span>
              <span class="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold" [ngClass]="awsStatusBadgeClass(s.connectionStatus)">{{ awsStatusLabel(s.connectionStatus) }}</span>
            </div>
            <div class="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-zinc-500">
              <span class="inline-flex items-center gap-1">
                <lucide-icon name="map-pin" [size]="10"></lucide-icon>{{ azureLocationSummary(s) }}
              </span>
              <span class="text-zinc-300">·</span>
              <span><span class="font-semibold text-zinc-700">{{ s.monitoredResourceCount }}</span> monitored</span>
              <ng-container *ngIf="s.serviceEntryCount">
                <span class="text-zinc-300">·</span>
                <span><span class="font-semibold text-zinc-700">{{ s.serviceEntryCount }}</span> resources</span>
              </ng-container>
            </div>
            <div *ngIf="s.failureReason" class="mt-1 text-[10px] text-red-600">{{ s.failureReason }}</div>

            <!-- Per-subscription support actions (parallel to AWS) -->
            <div class="mt-2 flex items-center gap-2 border-t border-zinc-100 pt-2">
              <button
                type="button"
                (click)="verifyAzureSubscription(s)"
                [disabled]="!!azureActionSubId"
                class="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium text-zinc-500 transition-colors hover:text-violet-600 disabled:opacity-50"
              >
                <lucide-icon name="refresh-cw" [size]="11" [strokeWidth]="2" [class.animate-spin]="azureActionSubId === s.subscriptionId"></lucide-icon>
                Verify
              </button>
              <button
                type="button"
                (click)="askDisconnectAzureSubscription(s)"
                [disabled]="!!azureActionSubId"
                class="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium text-zinc-400 transition-colors hover:text-red-600 disabled:opacity-50"
              >Disconnect</button>
            </div>
          </div>
          <p class="text-[10px] text-zinc-400">Location edits are managed from the customer dashboard.</p>
        </div>

        <!-- Action result / error banner -->
        <div *ngIf="azureActionResult" class="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[10px] text-emerald-800">{{ azureActionResult }}</div>
        <div *ngIf="azureActionError" class="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[10px] text-red-700">{{ azureActionError }}</div>

        <!-- Recent Azure lifecycle activity (audit trail) -->
        <div *ngIf="azureEvents.length > 0" class="mt-3">
          <p class="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Recent Azure activity</p>
          <div class="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
            <div *ngFor="let ev of azureEvents.slice(0, 8)" class="flex items-start gap-2 px-3 py-2">
              <span class="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full" [ngClass]="awsEventDotClass(ev.status)"></span>
              <div class="min-w-0 flex-1">
                <p class="text-[11px] text-zinc-700">
                  {{ azureEventLabel(ev.eventType) }}
                  <span *ngIf="ev.subscriptionId" class="ml-1 font-mono text-[10px] text-zinc-400">{{ ev.subscriptionId }}</span>
                </p>
                <p class="text-[10px] text-zinc-400">{{ formatRelativeTime(ev.timestamp) }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ── Section 3d: GCP projects (support-initiated) ─────────────── -->
      <section *ngIf="gcpSummary">
        <div class="mb-2 flex items-center justify-between">
          <p class="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Google Cloud projects</p>
          <span class="rounded-full px-1.5 py-0.5 text-[9px] font-semibold" [ngClass]="{
            'bg-emerald-100 text-emerald-700': gcpOverallState === 'healthy',
            'bg-amber-100 text-amber-700':     gcpOverallState === 'degraded',
            'bg-zinc-100 text-zinc-500':       gcpOverallState === 'none'
          }">{{ gcpOverallState === 'healthy' ? 'Healthy' : gcpOverallState === 'degraded' ? 'Degraded' : 'Not connected' }}</span>
        </div>

        <p *ngIf="gcpProjects.length === 0" class="rounded-lg border border-dashed border-zinc-200 px-3 py-2 text-[11px] text-zinc-400">
          No Google Cloud projects connected.
        </p>

        <div *ngIf="gcpProjects.length > 0" class="space-y-2">
          <div *ngFor="let p of gcpProjects"
               class="rounded-lg border bg-white px-3 py-2"
               [class.border-violet-200]="p.isPrimary"
               [class.border-zinc-200]="!p.isPrimary">
            <div class="flex items-center gap-2">
              <span class="h-1.5 w-1.5 flex-shrink-0 rounded-full" [ngClass]="awsHealthDotClass(p.healthStatus)"></span>
              <span class="min-w-0 flex-1 truncate font-mono text-xs font-medium text-zinc-800">{{ gcpProjectLabel(p) }}</span>
              <span *ngIf="p.isPrimary" class="flex-shrink-0 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700">Primary</span>
              <span class="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold" [ngClass]="awsStatusBadgeClass(p.connectionStatus)">{{ awsStatusLabel(p.connectionStatus) }}</span>
            </div>
            <div class="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-zinc-500">
              <span class="inline-flex items-center gap-1">
                <lucide-icon name="map-pin" [size]="10"></lucide-icon>{{ gcpRegionSummary(p) }}
              </span>
              <span class="text-zinc-300">·</span>
              <span><span class="font-semibold text-zinc-700">{{ p.monitoredResourceCount }}</span> monitored</span>
              <ng-container *ngIf="p.serviceEntryCount">
                <span class="text-zinc-300">·</span>
                <span><span class="font-semibold text-zinc-700">{{ p.serviceEntryCount }}</span> resources</span>
              </ng-container>
            </div>
            <div *ngIf="p.failureReason" class="mt-1 text-[10px] text-red-600">{{ p.failureReason }}</div>

            <!-- Per-project support actions (parallel to AWS/Azure) -->
            <div class="mt-2 flex items-center gap-2 border-t border-zinc-100 pt-2">
              <button
                type="button"
                (click)="verifyGcpProject(p)"
                [disabled]="!!gcpActionProjectId"
                class="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium text-zinc-500 transition-colors hover:text-violet-600 disabled:opacity-50"
              >
                <lucide-icon name="refresh-cw" [size]="11" [strokeWidth]="2" [class.animate-spin]="gcpActionProjectId === p.projectId"></lucide-icon>
                Verify
              </button>
              <button
                type="button"
                (click)="askDisconnectGcpProject(p)"
                [disabled]="!!gcpActionProjectId"
                class="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium text-zinc-400 transition-colors hover:text-red-600 disabled:opacity-50"
              >Disconnect</button>
            </div>
          </div>
          <p class="text-[10px] text-zinc-400">Region edits are managed from the customer dashboard.</p>
        </div>

        <!-- Action result / error banner -->
        <div *ngIf="gcpActionResult" class="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[10px] text-emerald-800">{{ gcpActionResult }}</div>
        <div *ngIf="gcpActionError" class="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[10px] text-red-700">{{ gcpActionError }}</div>

        <!-- Recent GCP lifecycle activity (audit trail) -->
        <div *ngIf="gcpEvents.length > 0" class="mt-3">
          <p class="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Recent Google Cloud activity</p>
          <div class="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
            <div *ngFor="let ev of gcpEvents.slice(0, 8)" class="flex items-start gap-2 px-3 py-2">
              <span class="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full" [ngClass]="awsEventDotClass(ev.status)"></span>
              <div class="min-w-0 flex-1">
                <p class="text-[11px] text-zinc-700">
                  {{ gcpEventLabel(ev.eventType) }}
                  <span *ngIf="ev.projectId" class="ml-1 font-mono text-[10px] text-zinc-400">{{ ev.projectId }}</span>
                </p>
                <p class="text-[10px] text-zinc-400">{{ formatRelativeTime(ev.timestamp) }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ── Disconnect-account confirmation dialog ─────────────────── -->
      <div *ngIf="awsConfirmDisconnect" class="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 px-4" role="dialog" aria-modal="true">
        <div class="w-full max-w-md overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl">
          <div class="flex items-start gap-3 px-5 py-4">
            <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
              <lucide-icon name="triangle-alert" [size]="16" class="text-red-600" [strokeWidth]="2"></lucide-icon>
            </div>
            <div class="min-w-0">
              <h3 class="text-sm font-semibold text-zinc-900">Disconnect AWS account {{ awsConfirmDisconnect.accountId }}?</h3>
              <p class="mt-1 text-xs text-zinc-500">
                This affects <strong>only this account</strong> — not the whole subscription. Its monitoring
                (services, configs, open findings) is removed and its quota slots are freed. Other accounts are untouched.
              </p>
              <p *ngIf="awsConfirmDisconnect.isPrimary" class="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                This is the <strong>primary</strong> account. Another connected account will be auto-promoted to primary; the subscription's external ID is unchanged.
              </p>
            </div>
          </div>
          <div class="flex items-center justify-end gap-2 border-t border-zinc-200 bg-zinc-50 px-5 py-3">
            <button type="button" (click)="cancelDisconnectAwsAccount()" [disabled]="!!awsActionAccountId" class="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 disabled:opacity-60">Cancel</button>
            <button type="button" (click)="confirmDisconnectAwsAccount()" [disabled]="!!awsActionAccountId" class="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60">Disconnect account</button>
          </div>
        </div>
      </div>

      <!-- ── Disconnect-subscription confirmation dialog (Azure) ─────── -->
      <div *ngIf="azureConfirmDisconnect" class="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 px-4" role="dialog" aria-modal="true">
        <div class="w-full max-w-md overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl">
          <div class="flex items-start gap-3 px-5 py-4">
            <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
              <lucide-icon name="triangle-alert" [size]="16" class="text-red-600" [strokeWidth]="2"></lucide-icon>
            </div>
            <div class="min-w-0">
              <h3 class="text-sm font-semibold text-zinc-900">Disconnect Azure subscription {{ azureConfirmDisconnect.subscriptionId }}?</h3>
              <p class="mt-1 text-xs text-zinc-500">
                This affects <strong>only this subscription</strong> — not the whole integration. Its monitoring
                (services, configs, open findings) is removed and its quota slots are freed. Other subscriptions are untouched.
              </p>
              <p *ngIf="azureConfirmDisconnect.isPrimary" class="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                This is the <strong>primary</strong> subscription. Another connected subscription will be auto-promoted to primary; the service principal is unchanged.
              </p>
            </div>
          </div>
          <div class="flex items-center justify-end gap-2 border-t border-zinc-200 bg-zinc-50 px-5 py-3">
            <button type="button" (click)="cancelDisconnectAzureSubscription()" [disabled]="!!azureActionSubId" class="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 disabled:opacity-60">Cancel</button>
            <button type="button" (click)="confirmDisconnectAzureSubscription()" [disabled]="!!azureActionSubId" class="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60">Disconnect subscription</button>
          </div>
        </div>
      </div>

      <!-- ── Disconnect-project confirmation dialog (GCP) ───────────── -->
      <div *ngIf="gcpConfirmDisconnect" class="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 px-4" role="dialog" aria-modal="true">
        <div class="w-full max-w-md overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl">
          <div class="flex items-start gap-3 px-5 py-4">
            <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
              <lucide-icon name="triangle-alert" [size]="16" class="text-red-600" [strokeWidth]="2"></lucide-icon>
            </div>
            <div class="min-w-0">
              <h3 class="text-sm font-semibold text-zinc-900">Disconnect GCP project {{ gcpConfirmDisconnect.projectId }}?</h3>
              <p class="mt-1 text-xs text-zinc-500">
                This affects <strong>only this project</strong> — not the whole integration. Its monitoring
                (services, configs, open findings) is removed and its quota slots are freed. Other projects are untouched.
              </p>
              <p *ngIf="gcpConfirmDisconnect.isPrimary" class="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                This is the <strong>primary</strong> project. Another connected project will be auto-promoted to primary; the service account is unchanged.
              </p>
            </div>
          </div>
          <div class="flex items-center justify-end gap-2 border-t border-zinc-200 bg-zinc-50 px-5 py-3">
            <button type="button" (click)="cancelDisconnectGcpProject()" [disabled]="!!gcpActionProjectId" class="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 disabled:opacity-60">Cancel</button>
            <button type="button" (click)="confirmDisconnectGcpProject()" [disabled]="!!gcpActionProjectId" class="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60">Disconnect project</button>
          </div>
        </div>
      </div>

      <!-- ── Section 4: Quick Stats ─────────────────────────────────── -->
      <section>
        <p class="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
          Quick Stats
        </p>

        <div class="grid grid-cols-3 gap-2">
          <div class="rounded-lg border border-zinc-100 bg-zinc-50 px-2 py-2.5 text-center">
            <p class="text-sm font-semibold text-zinc-900">{{ context.teamSize }}</p>
            <p class="mt-0.5 text-[10px] text-zinc-400">Members</p>
          </div>
          <div class="rounded-lg border border-zinc-100 bg-zinc-50 px-2 py-2.5 text-center">
            <p class="text-sm font-semibold text-zinc-900">{{ context.monitoredServicesCount }}</p>
            <p class="mt-0.5 text-[10px] text-zinc-400">Monitored</p>
          </div>
          <div class="rounded-lg border border-zinc-100 bg-zinc-50 px-2 py-2.5 text-center">
            <p class="text-sm font-semibold text-zinc-900">{{ connectedIntegrationCount }}</p>
            <p class="mt-0.5 text-[10px] text-zinc-400">Integrations</p>
          </div>
        </div>

        <p class="mt-3 text-[10px] text-zinc-400">
          Created {{ formatDate(context.organization.createdAt) }}
        </p>
      </section>

    </div>

    <!-- ── Tab: Team ──────────────────────────────────────────────────── -->
    <div *ngIf="activeTab === 'team'" class="flex-1 overflow-y-auto px-5 py-4">

      <!-- Section header with count -->
      <p class="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
        Team Members ({{ context.teamMembers.length }})
      </p>

      <!-- Empty state -->
      <div
        *ngIf="context.teamMembers.length === 0"
        class="flex flex-col items-center justify-center gap-2 rounded-lg border border-zinc-100 bg-zinc-50 py-12 text-center"
      >
        <lucide-icon name="users" [size]="28" [strokeWidth]="1.25" class="text-zinc-300"></lucide-icon>
        <p class="text-xs text-zinc-400">No team members found.</p>
      </div>

      <!-- Member list -->
      <div *ngIf="context.teamMembers.length > 0" class="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
        <div
          *ngFor="let member of context.teamMembers"
          class="flex items-center gap-3 px-4 py-2.5"
        >
          <!-- Initials avatar -->
          <div class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-violet-100 text-[10px] font-semibold text-violet-700">
            {{ memberInitials(member) }}
          </div>

          <!-- Name + email -->
          <div class="min-w-0 flex-1">
            <p class="truncate text-xs font-medium text-zinc-800">
              {{ (member.givenName || member.familyName) ? (member.givenName + ' ' + member.familyName).trim() : member.email }}
            </p>
            <p *ngIf="member.givenName || member.familyName" class="truncate text-[10px] text-zinc-400">{{ member.email }}</p>
          </div>

          <!-- Role badge -->
          <span
            class="flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold"
            [ngClass]="memberRoleBadgeClass(member.role)"
          >{{ memberRoleLabel(member.role) }}</span>
        </div>
      </div>

    </div>

    <!-- ── Tab: Monitored Services ────────────────────────────────────── -->
    <div *ngIf="activeTab === 'services'" class="flex-1 overflow-y-auto px-5 py-4">

      <!-- Section header with count -->
      <p class="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
        Monitored Services ({{ context.monitoredServices.length }})
      </p>

      <!-- Empty state -->
      <div
        *ngIf="context.monitoredServices.length === 0"
        class="flex flex-col items-center justify-center gap-2 rounded-lg border border-zinc-100 bg-zinc-50 py-12 text-center"
      >
        <lucide-icon name="activity" [size]="28" [strokeWidth]="1.25" class="text-zinc-300"></lucide-icon>
        <p class="text-xs text-zinc-400">No monitored services.</p>
      </div>

      <!-- Service list — grouped by cloud scope (AWS account / Azure subscription) -->
      <div *ngIf="context.monitoredServices.length > 0" class="space-y-3">
        <div *ngFor="let g of monitoredServicesByScope">
          <!-- Scope group header (provider-native noun) -->
          <p class="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
            <lucide-icon name="cloud" [size]="11" [strokeWidth]="1.75"></lucide-icon>
            <span class="font-mono normal-case tracking-normal">{{ g.label }}</span>
            <span class="text-zinc-300">·</span>
            <span class="normal-case tracking-normal">{{ g.scopeNoun }}</span>
            <span class="text-zinc-300">·</span>
            <span>{{ g.services.length }}</span>
          </p>

          <div class="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
            <div *ngFor="let svc of g.services" class="flex items-center gap-3 px-4 py-2.5">
              <lucide-icon [name]="serviceTypeIcon(svc.serviceType)" [size]="14" [strokeWidth]="1.75" class="flex-shrink-0 text-zinc-400"></lucide-icon>

              <div class="min-w-0 flex-1">
                <p class="truncate text-xs font-medium text-zinc-800">{{ svc.name }}</p>
                <p *ngIf="svc.name !== svc.resourceId" class="truncate font-mono text-[10px] text-zinc-400">{{ svc.resourceId }}</p>
                <p *ngIf="svc.region" class="truncate text-[10px] text-zinc-400">{{ svc.region }}</p>
              </div>

              <span class="flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold" [ngClass]="serviceTypeBadgeClass(svc.serviceType)">{{ serviceTypeLabel(svc.serviceType) }}</span>

              <span class="h-1.5 w-1.5 flex-shrink-0 rounded-full" [class.bg-emerald-500]="svc.isMonitored" [class.bg-zinc-300]="!svc.isMonitored" [title]="svc.isMonitored ? 'Monitored' : 'Not monitored'"></span>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- ── Tab: Monitoring ───────────────────────────────────────────────── -->
    <div *ngIf="activeTab === 'monitoring'" class="flex-1 overflow-y-auto px-5 py-4">

      <!-- Loading skeleton -->
      <ng-container *ngIf="monitoringLoading">
        <div class="animate-pulse space-y-3">
          <!-- Summary card skeletons -->
          <div class="grid grid-cols-2 gap-2">
            <div *ngFor="let _ of SKELETON_ITEMS.slice(0,4)" class="h-14 rounded-lg bg-zinc-100"></div>
          </div>
          <!-- Service row skeletons -->
          <div class="mt-3 divide-y divide-zinc-100 rounded-lg border border-zinc-100">
            <div *ngFor="let _ of SKELETON_ITEMS" class="flex items-center gap-3 px-4 py-3">
              <div class="h-3 w-3 rounded-full bg-zinc-100"></div>
              <div class="h-3 flex-1 rounded bg-zinc-100"></div>
              <div class="h-4 w-14 rounded bg-zinc-100"></div>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- Error state -->
      <div *ngIf="!monitoringLoading && monitoringError"
           class="flex flex-col items-center justify-center gap-3 rounded-lg border border-red-100 bg-red-50 py-8 text-center">
        <lucide-icon name="circle-alert" [size]="24" [strokeWidth]="1.25" class="text-red-400"></lucide-icon>
        <p class="text-xs text-red-600">{{ monitoringError }}</p>
        <button (click)="retryMonitoring()" class="text-xs font-medium text-violet-600 hover:underline">Retry</button>
      </div>

      <!-- Content -->
      <ng-container *ngIf="!monitoringLoading && !monitoringError && monitoringContext">

        <!-- ── Summary cards ──────────────────────────────────────────── -->
        <div class="mb-4 grid grid-cols-2 gap-2">

          <div class="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2.5 text-center">
            <p class="text-sm font-semibold text-emerald-800">{{ monitoringContext.summary.healthyCount }}</p>
            <p class="mt-0.5 text-[10px] text-emerald-600">Healthy</p>
          </div>

          <div class="rounded-lg border border-red-100 bg-red-50 px-3 py-2.5 text-center">
            <p class="text-sm font-semibold text-red-800">{{ monitoringContext.summary.degradedCount }}</p>
            <p class="mt-0.5 text-[10px] text-red-600">Unhealthy</p>
          </div>

          <div class="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2.5 text-center">
            <p class="text-sm font-semibold text-amber-800">{{ monitoringContext.summary.unknownCount }}</p>
            <p class="mt-0.5 text-[10px] text-amber-600">Unknown</p>
          </div>

          <div class="rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2.5 text-center">
            <p class="text-[10px] font-medium text-zinc-500">Last checked</p>
            <p class="mt-0.5 text-[11px] text-zinc-700">{{ formatRelativeTime(monitoringContext.summary.lastCheckedAt) }}</p>
          </div>

        </div>

        <!-- ── Services health list ───────────────────────────────────── -->
        <section class="mb-4">
          <p class="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
            Services ({{ monitoringContext.services.length }})
          </p>

          <div *ngIf="monitoringContext.services.length === 0"
               class="flex flex-col items-center justify-center gap-2 rounded-lg border border-zinc-100 bg-zinc-50 py-8 text-center">
            <lucide-icon name="shield" [size]="24" [strokeWidth]="1.25" class="text-zinc-300"></lucide-icon>
            <p class="text-xs text-zinc-400">No monitored services.</p>
          </div>

          <div *ngIf="monitoringContext.services.length > 0"
               class="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
            <div
              *ngFor="let svc of monitoringContext.services"
              class="flex items-center gap-3 px-4 py-2.5"
            >
              <!-- Health dot -->
              <span class="h-2 w-2 flex-shrink-0 rounded-full" [ngClass]="healthDotClass(svc.health)"></span>

              <!-- Icon -->
              <lucide-icon
                [name]="monitoringServiceIcon(svc)"
                [size]="13"
                [strokeWidth]="1.75"
                class="flex-shrink-0 text-zinc-400"
              ></lucide-icon>

              <!-- Name + mode -->
              <div class="min-w-0 flex-1">
                <p class="truncate text-xs font-medium text-zinc-800">{{ svc.serviceName }}</p>
                <p class="text-[10px] text-zinc-400">
                  {{ monitoringModeLabel(svc.monitoringMode) }}
                  <ng-container *ngIf="svc.lastCheckedAt">
                    · {{ formatRelativeTime(svc.lastCheckedAt) }}
                  </ng-container>
                </p>
                <p *ngIf="svc.accountId || svc.region" class="truncate text-[10px] text-zinc-400">
                  {{ svc.accountId || 'Primary' }}<span *ngIf="svc.region"> · {{ svc.region }}</span>
                </p>
              </div>

              <!-- Health badge -->
              <span
                class="flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold"
                [ngClass]="healthBadgeClass(svc.health)"
              >{{ healthLabel(svc.health) }}</span>

              <!-- Open finding count -->
              <ng-container *ngIf="svc.openFindingCount > 0">
                <span class="flex-shrink-0 rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-semibold text-red-700">
                  {{ svc.openFindingCount }}
                </span>
              </ng-container>
            </div>
          </div>
        </section>

        <!-- ── Findings section ───────────────────────────────────────── -->
        <section>
          <div class="mb-2 flex items-center justify-between">
            <p class="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Findings</p>
          </div>

          <!-- Filters -->
          <div class="mb-2 flex gap-2">
            <select
              [(ngModel)]="findingSeverityFilter"
              (change)="applyFindingFilters()"
              class="flex-1 rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] text-zinc-700 focus:border-violet-400 focus:outline-none"
            >
              <option value="">All severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
              <option value="INFO">Info</option>
            </select>

            <select
              [(ngModel)]="findingStateFilter"
              (change)="applyFindingFilters()"
              class="flex-1 rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] text-zinc-700 focus:border-violet-400 focus:outline-none"
            >
              <option value="">All states</option>
              <option value="OPEN">Open</option>
              <option value="RESOLVED">Resolved</option>
            </select>
          </div>

          <!-- Findings loading -->
          <div *ngIf="findingsLoading" class="animate-pulse space-y-1.5">
            <div *ngFor="let _ of SKELETON_ITEMS" class="h-12 rounded-lg bg-zinc-100"></div>
          </div>

          <!-- Findings error -->
          <div *ngIf="!findingsLoading && findingsError"
               class="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-center">
            <p class="text-xs text-red-600">{{ findingsError }}</p>
            <button (click)="applyFindingFilters()" class="mt-1 text-xs font-medium text-violet-600 hover:underline">Retry</button>
          </div>

          <!-- Empty findings -->
          <div *ngIf="!findingsLoading && !findingsError && monitoringFindings.length === 0"
               class="flex flex-col items-center justify-center gap-2 rounded-lg border border-zinc-100 bg-zinc-50 py-8 text-center">
            <lucide-icon name="circle-check" [size]="24" [strokeWidth]="1.25" class="text-emerald-400"></lucide-icon>
            <p class="text-xs text-zinc-400">No findings match the selected filters.</p>
          </div>

          <!-- Findings list -->
          <div *ngIf="!findingsLoading && !findingsError && monitoringFindings.length > 0"
               class="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
            <div
              *ngFor="let finding of monitoringFindings"
              class="px-4 py-2.5"
            >
              <div class="flex items-start gap-2">
                <!-- Severity badge -->
                <span
                  class="mt-0.5 flex-shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold"
                  [ngClass]="severityBadgeClass(finding.severity)"
                >{{ finding.severity }}</span>

                <!-- Title + meta -->
                <div class="min-w-0 flex-1">
                  <p class="truncate text-xs font-medium text-zinc-800">{{ finding.title }}</p>
                  <p class="mt-0.5 text-[10px] text-zinc-400">
                    {{ finding.serviceName }}
                    <ng-container *ngIf="finding.accountId || finding.region">
                      <span class="mx-0.5 text-zinc-300">·</span>
                      {{ finding.accountId || 'Primary' }}<span *ngIf="finding.region"> / {{ finding.region }}</span>
                    </ng-container>
                    <span class="mx-0.5 text-zinc-300">·</span>
                    {{ sourceLabel(finding.source) }}
                    <span class="mx-0.5 text-zinc-300">·</span>
                    {{ formatRelativeTime(finding.observedAt) }}
                  </p>
                </div>

                <!-- State -->
                <span
                  class="flex-shrink-0 text-[10px] font-medium"
                  [ngClass]="findingStateClass(finding.findingState)"
                >{{ findingStateLabel(finding.findingState) }}</span>
              </div>
            </div>
          </div>

        </section>

      </ng-container>

    </div>

  </ng-container>

</div>
